import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { Package, Clock, CheckCircle, Truck, Users, ArrowRight, Shield, Download, ExternalLink, QrCode } from "lucide-react";
import { API } from "../apiConfig";


const DashboardPage = () => {
  const { user, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [certificateStats, setCertificateStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certificatesLoading, setCertificatesLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
    fetchCertificates();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      const response = await axios.get(`${API}/my-certificates`, { 
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true 
      });
      setCertificates(response.data.certificates || []);
      setCertificateStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching certificates:", error);
    } finally {
      setCertificatesLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "paid":
        return <CheckCircle className="h-4 w-4 text-secondary" />;
      case "shipped":
        return <Truck className="h-4 w-4 text-primary" />;
      case "pending":
        return <Clock className="h-4 w-4 text-muted-foreground" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Payment Pending",
      paid: "Order Confirmed",
      shipped: "Shipped",
      delivered: "Delivered",
      cancelled: "Cancelled"
    };
    return labels[status] || status;
  };

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <Navigation />

      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-4">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-medium text-primary">
                    {user?.name?.charAt(0)}
                  </span>
                </div>
              )}
              <div>
                <h1 className="text-3xl font-medium" data-testid="dashboard-title">
                  Welcome, {user?.name}
                </h1>
                <p className="text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            {/* Quick Actions */}
            {user?.role === "buyer" && (
              <div className="flex gap-4 mt-6">
                <Link to="/register?role=promoter">
                  <Button variant="outline" className="rounded-full">
                    <Users className="h-4 w-4 mr-2" />
                    Become an Ambassador
                  </Button>
                </Link>
              </div>
            )}

            {user?.role === "promoter" && (
              <Link to="/promoter">
                <Button variant="outline" className="rounded-full mt-6">
                  <Users className="h-4 w-4 mr-2" />
                  View Promoter Dashboard
                </Button>
              </Link>
            )}
          </motion.div>

          <Separator className="mb-12" />

          {/* Orders & Certificates */}
          <Tabs defaultValue="orders" className="w-full">
            <TabsList className="mb-8">
              <TabsTrigger value="orders">My Orders</TabsTrigger>
              <TabsTrigger value="certificates" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                My Certificates
                {certificateStats?.total_certificates > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {certificateStats.total_certificates}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="orders">
              <Tabs defaultValue="all" className="w-full">
                <TabsList className="mb-6">
                  <TabsTrigger value="all">All Orders</TabsTrigger>
                  <TabsTrigger value="pending">Pending</TabsTrigger>
                  <TabsTrigger value="completed">Completed</TabsTrigger>
                </TabsList>

                <TabsContent value="all">
                  <OrdersList orders={orders} loading={loading} formatPrice={formatPrice} formatDate={formatDate} getStatusIcon={getStatusIcon} getStatusLabel={getStatusLabel} />
                </TabsContent>

                <TabsContent value="pending">
                  <OrdersList orders={orders.filter(o => o.status === "pending" || o.status === "paid")} loading={loading} formatPrice={formatPrice} formatDate={formatDate} getStatusIcon={getStatusIcon} getStatusLabel={getStatusLabel} />
                </TabsContent>

                <TabsContent value="completed">
                  <OrdersList orders={orders.filter(o => o.status === "delivered")} loading={loading} formatPrice={formatPrice} formatDate={formatDate} getStatusIcon={getStatusIcon} getStatusLabel={getStatusLabel} />
                </TabsContent>
              </Tabs>
            </TabsContent>

            <TabsContent value="certificates">
              <CertificatesList 
                certificates={certificates} 
                stats={certificateStats}
                loading={certificatesLoading} 
                formatDate={formatDate} 
              />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const OrdersList = ({ orders, loading, formatPrice, formatDate, getStatusIcon, getStatusLabel }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-card rounded-2xl p-6 border border-border">
            <div className="h-6 bg-muted rounded w-1/4 mb-4" />
            <div className="h-4 bg-muted rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-16">
        <Package className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
        <h3 className="text-xl font-medium mb-2">No orders yet</h3>
        <p className="text-muted-foreground mb-6">
          Start your collection with a piece of authentic Aboriginal art
        </p>
        <Link to="/products" data-testid="empty-orders-shop-btn">
          <Button className="rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90">
            Browse Collection
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {orders.map((order, index) => (
        <motion.div
          key={order.order_id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-card rounded-2xl p-6 border border-border"
          data-testid={`order-${order.order_id}`}
        >
          {/* Order Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {getStatusIcon(order.status)}
                <Badge variant={order.status === "paid" ? "default" : "secondary"}>
                  {getStatusLabel(order.status)}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Order #{order.order_id} • {formatDate(order.created_at)}
              </p>
            </div>
            <p className="text-xl font-medium">
              {formatPrice(order.total)}
            </p>
          </div>

          {/* Order Items */}
          <div className="space-y-4">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-muted shrink-0">
                  <img
                    src={item.image || "https://images.unsplash.com/photo-1663888672547-360e763e4ac7"}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/products/${item.product_id}`}>
                    <p className="font-medium hover:text-primary transition-colors truncate">
                      {item.title}
                    </p>
                  </Link>
                  <p className="text-sm text-muted-foreground">
                    Qty: {item.quantity} × {formatPrice(item.price)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

const CertificatesList = ({ certificates, stats, loading, formatDate }) => {
  const downloadPDF = async (certificateId) => {
    try {
      const response = await axios.get(`${API}/certificates/${certificateId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate_${certificateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse bg-card rounded-2xl p-6 border border-border">
            <div className="h-6 bg-muted rounded w-1/4 mb-4" />
            <div className="h-4 bg-muted rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (certificates.length === 0) {
    return (
      <div className="text-center py-16">
        <Shield className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
        <h3 className="text-xl font-medium mb-2">No certificates yet</h3>
        <p className="text-muted-foreground mb-6">
          Purchase artwork to receive certificates of authenticity
        </p>
        <Link to="/products" data-testid="empty-certificates-shop-btn">
          <Button className="rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90">
            Browse Collection
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-card rounded-xl p-4 border border-border text-center">
            <p className="text-2xl font-bold text-primary">{stats.total_certificates}</p>
            <p className="text-sm text-muted-foreground">Total Certificates</p>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border text-center">
            <div className="flex items-center justify-center gap-1">
              <Shield className="h-5 w-5 text-emerald-500" />
              <p className="text-2xl font-bold text-emerald-500">{stats.verified_certificates || stats.total_certificates}</p>
            </div>
            <p className="text-sm text-muted-foreground">Verified Authentic</p>
          </div>
        </div>
      )}

      {/* Certificate Cards */}
      {certificates.map((cert, index) => (
        <motion.div
          key={cert.certificate_id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-card rounded-2xl p-6 border border-border"
          data-testid={`certificate-${cert.certificate_id}`}
        >
          <div className="flex gap-6">
            {/* Artwork Image */}
            <div className="w-24 h-24 rounded-xl overflow-hidden bg-muted shrink-0">
              <img
                src={cert.product_image || "https://images.unsplash.com/photo-1663888672547-360e763e4ac7"}
                alt={cert.artwork_title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Certificate Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div>
                  <h3 className="font-medium text-lg truncate">{cert.artwork_title}</h3>
                  <p className="text-sm text-muted-foreground">by {cert.artist_name}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                </div>
              </div>

              {/* Certificate Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                <div>
                  <p className="text-muted-foreground text-xs">Token ID</p>
                  <p className="font-mono text-xs truncate">{cert.token_id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Edition</p>
                  <p className="font-medium">{cert.edition_info || "Original"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Issued</p>
                  <p>{formatDate(cert.minted_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Verification Code</p>
                  <p className="font-mono">{cert.verification_code}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={() => downloadPDF(cert.certificate_id)}
                  data-testid={`download-cert-${cert.certificate_id}`}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download PDF
                </Button>
                <Link to={`/certificate/${cert.certificate_id}`}>
                  <Button variant="outline" size="sm" className="rounded-full">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    View Details
                  </Button>
                </Link>
                <Link to={`/verify/${cert.verification_code}`}>
                  <Button variant="ghost" size="sm" className="rounded-full">
                    <QrCode className="h-4 w-4 mr-1" />
                    Verify
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default DashboardPage;
